import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { recordingsApi } from '../../api/flashbackApi/recordings.ts';
import { HttpError } from '../../api/flashbackApi/http.ts';
import type { PaginatedResult, Recording, RecordingListQuery } from '../../api/flashbackApi/types.ts';

interface UseRecordingsState {
  data: PaginatedResult<Recording> | null;
  isLoading: boolean;
  error: string | null;
}

interface UseRecordingsResult extends UseRecordingsState {
  refresh: () => void;
}

export function useRecordings(
  cameraId: string | null,
  query: Omit<RecordingListQuery, 'cameraId'>,
): UseRecordingsResult {
  const [state, setState] = useState<UseRecordingsState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const queryKey = JSON.stringify({ cameraId, query });
  const reloadCount = useRef(0);

  const refresh = useCallback(() => {
    reloadCount.current += 1;
    setState((s) => ({ ...s, isLoading: true }));
  }, []);

  useEffect(() => {
    const id = cameraId != null ? String(cameraId).trim() : '';
    if (!id) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }

    const controller = new AbortController();
    setState((s) => ({ ...s, isLoading: true, error: null }));

    recordingsApi
      .listForCamera(id, query, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ data, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load recordings';
        setState((s) => ({ data: s.data, isLoading: false, error: message }));
      });

    return () => controller.abort();
    // queryKey + reloadCount.current intentionally drive refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, reloadCount.current]);

  return { ...state, refresh };
}
